// client/src/pages/Admin/ResourceManager/ResourceManager.tsx
import React, { useState, useMemo, useCallback, useRef } from 'react';
import { ResourceStatus, Resource } from '../../../shared/models/Resource';
import { Layers, CheckCircle2, Eye, Download, ArrowUpDown, Search } from 'lucide-react';
import { useResources } from './hooks/useResources';
import { useDebounce } from './hooks/useDebounce';
import { useResourceFilters } from './hooks/useResourceFilters';
import { usePagination } from './hooks/usePagination';
import { useBulkSelection } from './hooks/useBulkSelection';
import { useToast } from './hooks/useToast';

import { ResourceHeader } from './components/ResourceHeader';
import { ResourceStats } from './components/ResourceStats';
import { ResourceToolbar } from './components/ResourceToolbar';
import { ResourceFilters } from './ResourceFilters';
import { ResourceGrid } from './components/ResourceGrid';
import { ResourcePagination } from './components/ResourcePagination';
import { ResourceEmptyState } from './components/ResourceEmptyState';
import { ResourceSkeleton } from './components/ResourceSkeleton';
import { BulkActionToolbar } from './components/BulkActionToolbar';
import { DeleteResourceModal } from './components/DeleteResourceModal';
import { ToastContainer } from './components/ToastContainer';
import { ResourceEditor } from '../ResourceEditor';

import { SORT_OPTIONS, DEFAULT_PAGE_SIZE, DEBOUNCE_DELAY } from './constants/resource.constants';
import { getStatusCounts, getTotalViews, getTotalDownloads, getFilterOptions } from './utils/resourceStatistics';
import { resourceMatchesSearch } from './utils/resourceSearch';
import { formatStatusLabel } from './utils/resourceFormatters';
import { getCategoryDisplay } from './utils/resourceHelpers';
import {
  bulkUpdateStatus,
  bulkDeleteResources,
  bulkToggleFeatured
} from './services/resourceService';

import { ModalState } from './types/resource.types';

export const ResourceManager: React.FC = () => {
  // Hooks
  const { resources, loading, error, loadResources, removeResource, updateStatus } = useResources();
  const { toasts, showToast, removeToast } = useToast();

  // Local state
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useDebounce(searchTerm, DEBOUNCE_DELAY);
  const [showFilters, setShowFilters] = useState<boolean>(false);
  const [isSearchFocused, setIsSearchFocused] = useState<boolean>(false);
  const [sortOption, setSortOption] = useState<string>('newest');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [openActionMenu, setOpenActionMenu] = useState<string | null>(null);
  const [editingResource, setEditingResource] = useState<Resource | null>(null);
  const [showEditor, setShowEditor] = useState<boolean>(false);
  const [isBulkActionLoading, setIsBulkActionLoading] = useState<boolean>(false);
  const [modalState, setModalState] = useState<ModalState | null>(null);

  // Filters
  const { filters, filterResources, updateFilter, resetFilters, hasActiveFilters, activeFilterCount } =
    useResourceFilters(resources, debouncedSearchTerm);

  // Filter options from resources
  const filterOptions = useMemo(() => getFilterOptions(resources), [resources]);

  // Sorting
  const sortResources = useCallback((items: Resource[]) => {
    const sorted = [...items];
    const option = SORT_OPTIONS.find(opt => opt.value === sortOption);

    switch (sortOption) {
      case 'newest':
        sorted.sort((a, b) => {
          const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
          const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
          return sortDirection === 'desc' ? dateB - dateA : dateA - dateB;
        });
        break;
      case 'oldest':
        sorted.sort((a, b) => {
          const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
          const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
          return sortDirection === 'asc' ? dateA - dateB : dateB - dateA;
        });
        break;
      case 'alphabetical':
      case 'alphabetical-desc':
        sorted.sort((a, b) => {
          const titleA = (a.title || '').toLowerCase();
          const titleB = (b.title || '').toLowerCase();
          const comparison = titleA.localeCompare(titleB);
          return sortDirection === 'asc' ? comparison : -comparison;
        });
        break;
      case 'recently-updated':
        sorted.sort((a, b) => {
          const dateA = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
          const dateB = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
          return sortDirection === 'desc' ? dateB - dateA : dateA - dateB;
        });
        break;
      case 'downloads':
        sorted.sort((a, b) => {
          const countA = a.downloadCount || 0;
          const countB = b.downloadCount || 0;
          return sortDirection === 'desc' ? countB - countA : countA - countB;
        });
        break;
      case 'views':
        sorted.sort((a, b) => {
          const viewsA = a.views || 0;
          const viewsB = b.views || 0;
          return sortDirection === 'desc' ? viewsB - viewsA : viewsA - viewsB;
        });
        break;
      case 'featured':
        sorted.sort((a, b) => {
          const featuredA = a.isFeatured ? 1 : 0;
          const featuredB = b.isFeatured ? 1 : 0;
          return sortDirection === 'desc' ? featuredB - featuredA : featuredA - featuredB;
        });
        break;
      default:
        break;
    }

    return sorted;
  }, [sortOption, sortDirection]);

  // Filter and sort resources
  const filteredResources = useMemo(() => {
    const filtered = filterResources(resources);
    return sortResources(filtered);
  }, [resources, filterResources, sortResources]);

  // Bulk selection
  const {
    selectedResources,
    selectedResourceIds,
    isAllSelected,
    toggleResourceSelection,
    toggleSelectAll,
    clearSelection
  } = useBulkSelection(filteredResources);

  const selectedIds = selectedResources;
  const selectedCount = selectedResourceIds.length;
  const selectedItems = filteredResources.filter(
    (resource) => resource._id && selectedResources.has(resource._id)
  );
  const selectedIdsList = selectedResourceIds;
  const toggleSelection = toggleResourceSelection;

  // Pagination
  const {
    currentPage,
    pageSize,
    totalPages,
    paginatedItems,
    startIndex,
    endIndex,
    pageNumbers,
    goToPage,
    goToFirstPage,
    goToLastPage,
    goToPreviousPage,
    goToNextPage,
    changePageSize,
    resetPage
  } = usePagination(filteredResources, DEFAULT_PAGE_SIZE);

  // Search suggestions
  const searchSuggestions = useMemo(() => {
    if (!searchTerm || searchTerm.length < 2) return [];

    const search = searchTerm.toLowerCase().trim();
    const suggestions = new Set<string>();

    resources.forEach(resource => {
      if ((resource.title ?? '').toLowerCase().includes(search)) {
        suggestions.add(resource.title || '');
      }

      const category = getCategoryDisplay(resource.category);
      if (category.toLowerCase().includes(search)) {
        suggestions.add(`Category: ${category}`);
      }

      (resource.tags ?? []).forEach((tag: string) => {
        if (tag.toLowerCase().includes(search)) {
          suggestions.add(`Tag: ${tag}`);
        }
      });

      (resource.authors ?? []).forEach((author: { name?: string }) => {
        if ((author.name ?? '').toLowerCase().includes(search)) {
          suggestions.add(`Author: ${author.name}`);
        }
      });
    });

    return Array.from(suggestions).slice(0, 8);
  }, [searchTerm, resources]);

  // Modal helpers
  const showModal = useCallback((
    type: 'danger' | 'warning' | 'info' | 'success',
    title: string,
    message: string,
    onConfirm: () => void,
    confirmText?: string,
    cancelText?: string
  ) => {
    setModalState({
      isOpen: true,
      type,
      title,
      message,
      confirmText,
      cancelText,
      onConfirm,
      isLoading: false
    });
  }, []);

  const closeModal = useCallback(() => {
    if (modalState) {
      setModalState({ ...modalState, isOpen: false });
      setTimeout(() => setModalState(null), 300);
    }
  }, [modalState]);

  const setModalLoading = useCallback((loading: boolean) => {
    if (modalState) {
      setModalState({ ...modalState, isLoading: loading });
    }
  }, [modalState]);

  // Handlers
  const handleDeleteResource = useCallback((id: string) => {
    const resourceToDelete = resources.find((r: Resource) => r._id === id);
    const resourceTitle = resourceToDelete?.title || 'this resource';

    showModal(
      'danger',
      'Delete Resource',
      `Are you sure you want to delete "${resourceTitle}"? This action cannot be undone.`,
      async () => {
        setModalLoading(true);
        try {
          await removeResource(id);
          closeModal();
          showToast(`"${resourceTitle}" deleted successfully`, 'success');
        } catch (err) {
          const errorMsg = err instanceof Error ? err.message : 'Failed to delete resource';
          showToast(errorMsg, 'error');
          setModalLoading(false);
        }
      },
      'Delete',
      'Cancel'
    );
  }, [resources, removeResource, showModal, closeModal, setModalLoading, showToast]);

  const handleBulkPublish = useCallback(() => {
    showModal(
      'warning',
      'Bulk Publish',
      `Are you sure you want to publish ${selectedIdsList.length} selected resource(s)?`,
      async () => {
        setModalLoading(true);
        try {
          await bulkUpdateStatus(selectedIdsList, ResourceStatus.PUBLISHED);
          await loadResources();
          clearSelection();
          closeModal();
          showToast(`${selectedIdsList.length} resource(s) published successfully`, 'success');
        } catch (err) {
          const errorMsg = err instanceof Error ? err.message : 'Failed to publish resources';
          showToast(errorMsg, 'error');
          setModalLoading(false);
        }
      },
      'Publish',
      'Cancel'
    );
  }, [selectedIdsList, loadResources, clearSelection, showModal, closeModal, setModalLoading, showToast]);

  const handleBulkArchive = useCallback(() => {
    showModal(
      'warning',
      'Bulk Archive',
      `Are you sure you want to archive ${selectedIdsList.length} selected resource(s)?`,
      async () => {
        setModalLoading(true);
        try {
          await bulkUpdateStatus(selectedIdsList, ResourceStatus.ARCHIVED);
          await loadResources();
          clearSelection();
          closeModal();
          showToast(`${selectedIdsList.length} resource(s) archived successfully`, 'success');
        } catch (err) {
          const errorMsg = err instanceof Error ? err.message : 'Failed to archive resources';
          showToast(errorMsg, 'error');
          setModalLoading(false);
        }
      },
      'Archive',
      'Cancel'
    );
  }, [selectedIdsList, loadResources, clearSelection, showModal, closeModal, setModalLoading, showToast]);

  const handleBulkFeature = useCallback(() => {
    showModal(
      'warning',
      'Bulk Feature',
      `Are you sure you want to feature ${selectedIdsList.length} selected resource(s)?`,
      async () => {
        setModalLoading(true);
        try {
          await bulkToggleFeatured(selectedIdsList, true);
          await loadResources();
          clearSelection();
          closeModal();
          showToast(`${selectedIdsList.length} resource(s) featured successfully`, 'success');
        } catch (err) {
          const errorMsg = err instanceof Error ? err.message : 'Failed to feature resources';
          showToast(errorMsg, 'error');
          setModalLoading(false);
        }
      },
      'Feature',
      'Cancel'
    );
  }, [selectedIdsList, loadResources, clearSelection, showModal, closeModal, setModalLoading, showToast]);

  const handleBulkUnfeature = useCallback(() => {
    showModal(
      'warning',
      'Bulk Unfeature',
      `Are you sure you want to unfeature ${selectedIdsList.length} selected resource(s)?`,
      async () => {
        setModalLoading(true);
        try {
          await bulkToggleFeatured(selectedIdsList, false);
          await loadResources();
          clearSelection();
          closeModal();
          showToast(`${selectedIdsList.length} resource(s) unfeatured successfully`, 'success');
        } catch (err) {
          const errorMsg = err instanceof Error ? err.message : 'Failed to unfeature resources';
          showToast(errorMsg, 'error');
          setModalLoading(false);
        }
      },
      'Unfeature',
      'Cancel'
    );
  }, [selectedIdsList, loadResources, clearSelection, showModal, closeModal, setModalLoading, showToast]);

  const handleBulkDelete = useCallback(() => {
    showModal(
      'danger',
      'Bulk Delete',
      `Are you sure you want to permanently delete ${selectedIdsList.length} selected resource(s)? This action cannot be undone.`,
      async () => {
        setModalLoading(true);
        try {
          await bulkDeleteResources(selectedIdsList);
          await loadResources();
          clearSelection();
          closeModal();
          showToast(`${selectedIdsList.length} resource(s) deleted successfully`, 'success');
        } catch (err) {
          const errorMsg = err instanceof Error ? err.message : 'Failed to delete resources';
          showToast(errorMsg, 'error');
          setModalLoading(false);
        }
      },
      'Delete All',
      'Cancel'
    );
  }, [selectedIdsList, loadResources, clearSelection, showModal, closeModal, setModalLoading, showToast]);

  const handleBulkExport = useCallback(() => {
    const exportData = selectedItems.map(resource => ({
      id: resource._id,
      title: resource.title,
      description: resource.description,
      status: resource.status,
      category: resource.category,
      authors: resource.authors?.map(a => a.name).join(', '),
      tags: resource.tags?.join(', '),
      publishedDate: resource.publishedDate,
      views: resource.views,
      downloadCount: resource.downloadCount,
      isFeatured: resource.isFeatured,
      createdAt: resource.createdAt,
      updatedAt: resource.updatedAt
    }));

    const jsonStr = JSON.stringify(exportData, null, 2);
    const blob = new Blob([jsonStr], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `resources-export-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
    showToast(`${selectedIdsList.length} resource(s) exported successfully`, 'success');
  }, [selectedItems, selectedIdsList.length, showToast]);

  // ✅ UPDATED: Safe version of getResourceMatchFields
  const getResourceMatchFields = useCallback((resource: Resource): string[] => {
    // If no search term, return empty array
    if (!debouncedSearchTerm) return [];
    
    // If resource is invalid, return empty array
    if (!resource) return [];
    
    try {
      const result = resourceMatchesSearch(resource, debouncedSearchTerm);
      // Ensure we return an array
      if (result && Array.isArray(result.matchFields)) {
        return result.matchFields;
      }
      return [];
    } catch (error) {
      console.error('Error in getResourceMatchFields:', error);
      return [];
    }
  }, [debouncedSearchTerm]);

  // Stats
  const statusCounts = getStatusCounts(resources);
  const totalViews = getTotalViews(resources);
  const totalDownloads = getTotalDownloads(resources);

  const statCards = [
    {
      label: 'Total Resources',
      value: statusCounts.all,
      icon: Layers,
      color: '#3B82F6',
      bgColor: '#EFF6FF'
    },
    {
      label: 'Published',
      value: statusCounts[ResourceStatus.PUBLISHED],
      icon: CheckCircle2,
      color: '#10B981',
      bgColor: '#ECFDF5'
    },
    {
      label: 'Total Views',
      value: totalViews,
      icon: Eye,
      color: '#8B5CF6',
      bgColor: '#F5F3FF'
    },
    {
      label: 'Total Downloads',
      value: totalDownloads,
      icon: Download,
      color: '#F59E0B',
      bgColor: '#FFFBEB'
    }
  ];

  const currentSortLabel = SORT_OPTIONS.find(opt => opt.value === sortOption)?.label || 'Newest First';

  return (
    <div className="resource-manager">
      <ToastContainer toasts={toasts} onRemoveToast={removeToast} />

      {modalState && (
        <DeleteResourceModal
          isOpen={modalState.isOpen}
          onClose={closeModal}
          onConfirm={modalState.onConfirm}
          title={modalState.title}
          message={modalState.message}
          confirmText={modalState.confirmText}
          cancelText={modalState.cancelText}
          type={modalState.type}
          isLoading={modalState.isLoading}
        />
      )}

      <ResourceHeader
        onRefresh={loadResources}
        onNewResource={() => {
          setEditingResource(null);
          setShowEditor(true);
        }}
      />

      <ResourceStats stats={statCards} />

      {error && (
        <div className="error-message" role="alert">
          <span className="error-icon">⚠</span>
          {error}
        </div>
      )}

      <ResourceToolbar
        showFilters={showFilters}
        onToggleFilters={() => setShowFilters(!showFilters)}
        activeFilterCount={activeFilterCount}
        hasActiveFilters={hasActiveFilters}
        onResetFilters={resetFilters}
        sortOption={sortOption}
        sortDirection={sortDirection}
        onSortChange={(value) => {
          setSortOption(value);
          const option = SORT_OPTIONS.find(opt => opt.value === value);
          setSortDirection(option?.defaultDirection === 'asc' ? 'asc' : 'desc');
        }}
        onToggleSortDirection={() => {
          setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
        }}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        onClearSearch={() => {
          setSearchTerm('');
          setDebouncedSearchTerm('');
        }}
        isSearchFocused={isSearchFocused}
        onSearchFocus={() => setIsSearchFocused(true)}
        onSearchBlur={() => setIsSearchFocused(false)}
      />

      {showFilters && (
        <ResourceFilters
          filters={filters}
          filterOptions={filterOptions}
          onFilterChange={updateFilter}
          hasActiveFilters={hasActiveFilters}
        />
      )}

      <BulkActionToolbar
        selectedCount={selectedCount}
        isBulkActionLoading={isBulkActionLoading}
        onPublish={handleBulkPublish}
        onArchive={handleBulkArchive}
        onFeature={handleBulkFeature}
        onUnfeature={handleBulkUnfeature}
        onExport={handleBulkExport}
        onDelete={handleBulkDelete}
        onClear={clearSelection}
      />

      <div className="results-count">
        <span className="results-count-text">
          Showing <strong>{startIndex}-{endIndex}</strong> of <strong>{filteredResources.length}</strong> resources
          {hasActiveFilters && ' (filtered)'}
        </span>
        <div className="results-count-right">
          <span className="sort-info">
            <ArrowUpDown size={12} />
            Sorted by: <strong>{currentSortLabel}</strong>
            {sortDirection === 'asc' ? ' ↑' : ' ↓'}
          </span>
          {debouncedSearchTerm && (
            <span className="search-stats">
              <span className="search-stat-badge">
                <Search size={12} />
                {debouncedSearchTerm}
              </span>
              <span className="search-stat-divider">•</span>
              <span className="search-stat-text">
                {filteredResources.length} result{filteredResources.length !== 1 ? 's' : ''}
              </span>
            </span>
          )}
        </div>
      </div>

      {loading ? (
        <ResourceSkeleton />
      ) : paginatedItems.length === 0 ? (
        <ResourceEmptyState
          hasActiveFilters={hasActiveFilters}
          onNewResource={() => {
            setEditingResource(null);
            setShowEditor(true);
          }}
        />
      ) : (
        <>
          <ResourceGrid
            resources={paginatedItems}
            selectedIds={selectedIds}
            onToggleSelection={toggleSelection}
            onEdit={(resource) => {
              setEditingResource(resource);
              setShowEditor(true);
            }}
            onDelete={handleDeleteResource}
            onStatusChange={updateStatus}
            onToggleActionMenu={(id, e) => {
              e.stopPropagation();
              setOpenActionMenu(prev => prev === id ? null : id);
            }}
            openActionMenu={openActionMenu}
            getMatchFields={getResourceMatchFields}
            debouncedSearchTerm={debouncedSearchTerm}
          />

          <ResourcePagination
            currentPage={currentPage}
            totalPages={totalPages}
            pageSize={pageSize}
            startIndex={startIndex}
            endIndex={endIndex}
            totalItems={filteredResources.length}
            pageNumbers={pageNumbers}
            onGoToPage={goToPage}
            onGoToFirstPage={goToFirstPage}
            onGoToLastPage={goToLastPage}
            onGoToPreviousPage={goToPreviousPage}
            onGoToNextPage={goToNextPage}
            onPageSizeChange={changePageSize}
          />
        </>
      )}

      {showEditor && (
        <ResourceEditor
          resource={editingResource || undefined}
          onSave={async () => {
            await loadResources();
            setShowEditor(false);
            setEditingResource(null);
            showToast('Resource saved successfully', 'success');
          }}
          onCancel={() => {
            setShowEditor(false);
            setEditingResource(null);
          }}
        />
      )}
    </div>
  );
};